import React, { useState, useEffect } from 'react';
import useWallet from './useWallet';
import WalletSalonGroup from './WalletSalonGroup';
import WalletCardDetail from './WalletCardDetail';
import ChooseDesignModal from './ChooseDesignModal';
import AddGiftCardModal from './AddGiftCardModal';
import './Wallet.css';

const GiftIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="8" width="18" height="13" rx="1.5" />
    <path d="M3 12h18M12 8v13" />
    <path d="M12 8S10.5 3.5 8 4.2C6.2 4.7 6.5 7.5 9 8M12 8s1.5-4.5 4-3.8c1.8.5 1.5 3.3-1 3.8" />
  </svg>
);

const BackArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24" aria-hidden>
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// Wallet — the signed-in customer's gift cards across salons, grouped by salon.
// Lives inside the Account area (/dashboard/wallet). Built so per-salon loyalty
// schemes slot into the same per-salon groups next. Cards bought in salon /
// gifted without a design can be personalised here; codes can be added/claimed.
// `embedded` = rendered as the Wallet sub-tab inside WalletHub: the hub owns
// the page header, and loyalty lives in its own sibling tab, so here we show
// gift cards only and drop the standalone header.
const Wallet = ({ history, embedded = false }) => {
  const { loading, items, groups, setDesign, addCard } = useWallet();
  const [active, setActive] = useState(null);     // detail sheet
  const [designing, setDesigning] = useState(null); // choosing a design for a card
  const [adding, setAdding] = useState(false);     // add-by-code modal

  // In the hub, gift cards only (loyalty is the neighbouring tab).
  const giftItems = embedded ? items.filter((i) => i.kind === 'gift-card') : items;
  const shownGroups = embedded
    ? groups
      .map((g) => ({ ...g, items: g.items.filter((i) => i.kind === 'gift-card') }))
      .filter((g) => g.items.length)
    : groups;

  // Route changes don't reset scroll, so arriving from a scrolled Account screen
  // would land mid-page. Start at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
    const main = document.querySelector('.dashboard-main');
    if (main) main.scrollTop = 0;
  }, []);

  const onDesignChosen = (designId) => {
    if (designing) setDesign(designing.id, designId);
    setDesigning(null);
  };

  // After claiming, drop the card into the wallet; if it has no design yet,
  // go straight into choosing one.
  const onClaimed = (walletItem) => {
    setAdding(false);
    addCard(walletItem);
    if (!walletItem.designId) setDesigning(walletItem);
  };

  return (
    <div className={embedded ? 'wallet-page' : 'dashboard-fade-in wallet-page'}>
      {!embedded && (
        <div className="dashboard-page-header">
          <button
            type="button"
            className="dashboard-back-button"
            onClick={() => history.push('/dashboard/account')}
            aria-label="Back to account"
          >
            <BackArrowIcon />
          </button>
          <div className="dashboard-page-header-text">
            <h1 className="dashboard-page-title">Wallet</h1>
            <p className="dashboard-page-subtitle">Your gift cards and loyalty cards across salons</p>
          </div>
        </div>
      )}

      {loading ? (
        /* Skeleton mirroring the real layout (salon header + two cards) -
           the last client screen still showing a bare spinner. */
        <div className="wallet-skeleton" aria-hidden>
          <div className="wallet-skel-row">
            <span className="wallet-skel-avatar" />
            <span className="wallet-skel-line" style={{ width: '40%' }} />
          </div>
          <div className="wallet-skel-card" />
          <div className="wallet-skel-line" style={{ width: '30%' }} />
          <div className="wallet-skel-card" />
        </div>
      ) : giftItems.length === 0 ? (
        <div className="wallet-empty">
          <div className="wallet-empty-icon"><GiftIcon /></div>
          <h2 className="wallet-empty-title">No gift cards yet</h2>
          <p className="wallet-empty-text">Gift cards you buy and are sent will appear here, ready to spend.</p>
          <button type="button" className="wallet-cta wallet-empty-cta" onClick={() => setAdding(true)}>
            Add a gift card
          </button>
          <button type="button" className="wallet-empty-link" onClick={() => history.push('/')}>
            Browse salons
          </button>
        </div>
      ) : (
        <>
          <div className="wallet-groups">
            {shownGroups.map((g) => (
              <WalletSalonGroup key={g.salonId} group={g} onCardClick={setActive} />
            ))}
          </div>
          <button type="button" className="wallet-add" onClick={() => setAdding(true)}>
            <PlusIcon /> Add a gift card
          </button>
        </>
      )}

      {active && (
        <WalletCardDetail
          item={active}
          onClose={() => setActive(null)}
          onChooseDesign={(item) => { setActive(null); setDesigning(item); }}
        />
      )}
      {designing && (
        <ChooseDesignModal item={designing} onClose={() => setDesigning(null)} onChosen={onDesignChosen} />
      )}
      {adding && <AddGiftCardModal onClose={() => setAdding(false)} onClaimed={onClaimed} />}
    </div>
  );
};

export default Wallet;
